import { supabase } from "~/integrations/supabase/client";

export interface NullifyCreditNoteRequest {
  sales_request_document_id: string;
  motive: string;
  nullification_type?: "full" | "administrative";
  administrative_reason?: string;
}

export interface NullifyCreditNoteResponse {
  success: boolean;
  message: string;
  nullification_type: string;
  bsale: any;
  nullificationDocument: any;
  originalCreditNote: any;
  documentError?: string;
  updateError?: string;
}

export class CreditNoteNullificationService {
  /**
   * Nullifies a credit note through the Bsale API
   * @param request The nullification request parameters
   * @returns Promise with the nullification response
   */
  static async nullifyCreditNote(
    request: NullifyCreditNoteRequest
  ): Promise<NullifyCreditNoteResponse> {
    try {
      console.log("🔄 Nullifying credit note:", request);

      // Call the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke(
        "anular-nota-credito",
        {
          body: request,
        }
      );

      if (error) {
        throw new Error(`Supabase function error: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || "Unknown error occurred");
      }

      console.log("✅ Credit note nullified successfully:", data);
      return data;
    } catch (error) {
      console.error("❌ Error nullifying credit note:", error);
      throw error;
    }
  }

  /**
   * Validates a nullification request before submitting
   * @param request The nullification request to validate
   * @returns Array of validation errors, empty if valid
   */
  static validateNullificationRequest(
    request: Partial<NullifyCreditNoteRequest>
  ): string[] {
    const errors: string[] = [];

    if (!request.sales_request_document_id) {
      errors.push("Sales request document ID is required");
    }

    if (!request.motive || request.motive.trim().length === 0) {
      errors.push("Nullification motive is required");
    }

    if (request.motive && request.motive.trim().length < 10) {
      errors.push("Nullification motive must be at least 10 characters long");
    }

    if (
      request.nullification_type === "administrative" &&
      (!request.administrative_reason ||
        request.administrative_reason.trim().length === 0)
    ) {
      errors.push(
        "Administrative reason is required for administrative nullifications"
      );
    }

    return errors;
  }

  /**
   * Checks if a credit note can be nullified
   * @param creditNote The credit note document to check
   * @returns Object with canNullify boolean and reason if not
   */
  static canNullify(creditNote: any): {
    canNullify: boolean;
    reason?: string;
  } {
    if (!creditNote) {
      return {
        canNullify: false,
        reason: "Credit note not found",
      };
    }

    if (creditNote.document_type !== "nota_de_credito") {
      return {
        canNullify: false,
        reason: "Document is not a credit note",
      };
    }

    if (creditNote.cancelled) {
      return {
        canNullify: false,
        reason: "Credit note is already nullified",
      };
    }

    if (!creditNote.external_id) {
      return {
        canNullify: false,
        reason: "No Bsale document ID found",
      };
    }

    if (creditNote.state === 0) {
      return {
        canNullify: false,
        reason: "Credit note is already cancelled",
      };
    }

    return {
      canNullify: true,
    };
  }

  /**
   * Gets nullification types with their descriptions
   * @returns Array of nullification types
   */
  static getNullificationTypes() {
    return [
      {
        value: "full",
        label: "Anulación Completa",
        description:
          "Anula completamente la nota de crédito, revirtiendo todos los valores.",
        color: "red",
      },
      {
        value: "administrative",
        label: "Anulación Administrativa",
        description:
          "Anulación por motivos administrativos con monto específico.",
        color: "purple",
      },
    ];
  }
}
